import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/constants";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Search, Shield, AlertTriangle, MapPin, Mail, Phone, Globe, Activity, FileText, CheckCircle, XCircle, Clock, Send } from "lucide-react";
import LinkToCase from "@/components/shared/LinkToCase";

const SCOPE_OPTIONS = [
  { id: "identity", label: "Identity Verification", icon: Shield },
  { id: "fraud", label: "Fraud Detection", icon: AlertTriangle },
  { id: "digital", label: "Digital Intelligence", icon: Globe },
  { id: "location", label: "Location Analysis", icon: MapPin },
];

const RiskBadge = ({ level, score }) => {
  const colors = {
    CRITICAL: "bg-red-600 text-white",
    HIGH: "bg-orange-500 text-white",
    ELEVATED: "bg-yellow-500 text-white",
    LOW: "bg-green-600 text-white",
  };
  return (
    <span className={`px-3 py-1 rounded-full text-sm font-bold ${colors[level] || "bg-gray-500 text-white"}`}>
      {level} — {score}/100
    </span>
  );
};

const SeverityDot = ({ severity }) => {
  const colors = { critical: "bg-red-500", high: "bg-orange-500", medium: "bg-yellow-500", low: "bg-green-500", info: "bg-blue-500" };
  return <span className={`inline-block w-2.5 h-2.5 rounded-full ${colors[severity] || "bg-gray-400"}`} />;
};

export default function LiveInvestigatorPage() {
  const [activeTab, setActiveTab] = useState("new");
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [history, setHistory] = useState([]);
  const [form, setForm] = useState({
    subject_name: "", subject_type: "individual",
    email: "", phone: "", ip_address: "", address: "",
    business_name: "", domain: "",
    case_type: "", jurisdiction: "", notes: "",
    investigation_scope: ["identity", "fraud", "digital", "location"],
  });

  const loadHistory = async () => {
    try {
      const { data } = await axios.get(`${API}/live-investigator/investigations`);
      setHistory(data.investigations || []);
    } catch {}
  };

  useEffect(() => { loadHistory(); }, []);

  const toggleScope = (id) => {
    setForm(f => ({
      ...f,
      investigation_scope: f.investigation_scope.includes(id)
        ? f.investigation_scope.filter(s => s !== id)
        : [...f.investigation_scope, id]
    }));
  };

  const runInvestigation = async () => {
    if (!form.subject_name) return;
    setLoading(true);
    setReport(null);
    try {
      const { data } = await axios.post(`${API}/live-investigator/investigate`, form);
      setReport(data);
      setActiveTab("results");
      loadHistory();
    } catch (err) {
      alert(err.response?.data?.detail || "Investigation failed");
    } finally {
      setLoading(false);
    }
  };

  const viewReport = (inv) => {
    setReport(inv);
    setActiveTab("results");
  };

  return (
    <div className="space-y-6" data-testid="live-investigator-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Live Investigator</h1>
        <p className="text-slate-500 mt-1">Real-time API-powered investigations with actionable intelligence</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="new" data-testid="tab-new-investigation">New Investigation</TabsTrigger>
          <TabsTrigger value="results" data-testid="tab-results">Results</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">History ({history.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="new" className="space-y-4 mt-4">
          <Card>
            <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Search className="w-5 h-5 text-blue-600" />Subject Information</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Subject Name *</Label>
                  <Input data-testid="subject-name" value={form.subject_name} onChange={e => setForm(f => ({...f, subject_name: e.target.value}))} placeholder="Full name or business name" />
                </div>
                <div>
                  <Label>Subject Type</Label>
                  <Select value={form.subject_type} onValueChange={val => setForm(f => ({...f, subject_type: val}))}>
                    <SelectTrigger data-testid="subject-type-select" className="h-10">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="individual">Individual</SelectItem>
                      <SelectItem value="business">Business</SelectItem>
                      <SelectItem value="property">Property</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><Label>Email Address</Label><Input data-testid="subject-email" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} placeholder="subject@example.com" /></div>
                <div><Label>Phone Number</Label><Input data-testid="subject-phone" value={form.phone} onChange={e => setForm(f => ({...f, phone: e.target.value}))} placeholder="+1 555-0123" /></div>
                <div><Label>IP Address</Label><Input data-testid="subject-ip" value={form.ip_address} onChange={e => setForm(f => ({...f, ip_address: e.target.value}))} placeholder="192.168.1.1" /></div>
                <div><Label>Physical Address</Label><Input data-testid="subject-address" value={form.address} onChange={e => setForm(f => ({...f, address: e.target.value}))} placeholder="123 Main St, City, State" /></div>
                <div><Label>Business Domain</Label><Input data-testid="subject-domain" value={form.domain} onChange={e => setForm(f => ({...f, domain: e.target.value}))} placeholder="company.com" /></div>
                <div><Label>Case Type</Label><Input data-testid="subject-case-type" value={form.case_type} onChange={e => setForm(f => ({...f, case_type: e.target.value}))} placeholder="Fraud, PI, Insurance..." /></div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-lg">Investigation Scope</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {SCOPE_OPTIONS.map(s => {
                  const active = form.investigation_scope.includes(s.id);
                  const Icon = s.icon;
                  return (
                    <div key={s.id}
                      role="button"
                      tabIndex={0}
                      data-testid={`scope-toggle-${s.id}`}
                      onClick={() => toggleScope(s.id)}
                      onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggleScope(s.id); } }}
                      className={`p-3 rounded-lg border-2 text-left transition-all cursor-pointer select-none ${active ? "border-blue-600 bg-blue-50" : "border-slate-200 hover:border-slate-300"}`}>
                      <div className="flex items-center justify-between">
                        <Icon className={`w-5 h-5 mb-1 ${active ? "text-blue-600" : "text-slate-400"}`} />
                        <Switch checked={active} onCheckedChange={() => toggleScope(s.id)} data-testid={`scope-switch-${s.id}`} />
                      </div>
                      <div className="text-sm font-medium mt-1">{s.label}</div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          <Button data-testid="run-investigation-btn" onClick={runInvestigation} disabled={loading || !form.subject_name} className="w-full bg-blue-600 hover:bg-blue-700 text-white h-12 text-lg">
            {loading ? <><Activity className="w-5 h-5 animate-spin mr-2" /> Running Live Investigation...</> : <><Search className="w-5 h-5 mr-2" /> Run Live Investigation</>}
          </Button>
        </TabsContent>

        <TabsContent value="results" className="mt-4">
          {report ? (
            <div className="space-y-4">
              <Card className="border-l-4 border-l-blue-600">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div>
                      <h2 className="text-xl font-bold">{report.subject?.name}</h2>
                      <p className="text-slate-500">{report.subject?.type} | {report.case_type || "General"} | {report.total_apis_queried} sources queried</p>
                    </div>
                    <RiskBadge level={report.risk_assessment?.level} score={report.risk_assessment?.score} />
                  </div>
                </CardContent>
              </Card>

              {report.risk_assessment?.factors?.length > 0 && (
                <Card className="border-red-200 bg-red-50">
                  <CardHeader><CardTitle className="text-lg text-red-800 flex items-center gap-2"><AlertTriangle className="w-5 h-5" />Risk Factors</CardTitle></CardHeader>
                  <CardContent>
                    <ul className="space-y-1">
                      {report.risk_assessment.factors.map((f, i) => (
                        <li key={i} className="text-red-700 text-sm flex items-start gap-2"><XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />{f}</li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}

              <Card>
                <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Activity className="w-5 h-5 text-blue-600" />Investigation Steps ({report.investigation_steps?.length})</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  {report.investigation_steps?.map((step, i) => (
                    <div key={i} className="p-3 bg-slate-50 rounded-lg border">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold text-sm">{step.step}</span>
                        <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">{step.source}</span>
                      </div>
                      <div className="text-xs text-slate-600 space-y-1">
                        {Object.entries(step.result || {}).filter(([k]) => k !== "analysis" && k !== "citations").map(([k, v]) => (
                          <div key={k} className="flex gap-2"><span className="font-medium text-slate-500 min-w-[100px]">{k.replace(/_/g, " ")}:</span><span>{String(v)}</span></div>
                        ))}
                        {step.result?.analysis && <div className="mt-2 p-2 bg-white rounded text-sm whitespace-pre-wrap">{step.result.analysis}</div>}
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card>
                  <CardHeader><CardTitle className="text-lg flex items-center gap-2"><FileText className="w-5 h-5 text-blue-600" />Findings ({report.findings?.length})</CardTitle></CardHeader>
                  <CardContent className="space-y-2">
                    {report.findings?.map((f, i) => (
                      <div key={i} className="flex items-start gap-2 text-sm">
                        <SeverityDot severity={f.severity} />
                        <span className="font-medium text-slate-600">[{f.category}]</span>
                        <span>{f.detail}</span>
                      </div>
                    ))}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader><CardTitle className="text-lg flex items-center gap-2"><CheckCircle className="w-5 h-5 text-green-600" />Action Plan</CardTitle></CardHeader>
                  <CardContent>
                    <ol className="space-y-2">
                      {report.action_plan?.map((a, i) => (
                        <li key={i} className="text-sm flex items-start gap-2">
                          <span className="text-blue-600 font-bold">{i + 1}.</span>{a}
                        </li>
                      ))}
                    </ol>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardContent className="pt-4">
                  <div className="flex flex-wrap gap-2 text-xs text-slate-500">
                    <span>Sources:</span>
                    {report.sources_used?.map((s, i) => <span key={i} className="px-2 py-0.5 bg-blue-50 rounded text-blue-600">{s}</span>)}
                  </div>
                </CardContent>
              </Card>

              <LinkToCase
                itemId={report.id}
                itemType="investigation"
                title={`Investigation: ${report.subject?.name}`}
                summary={`Risk: ${report.risk_assessment?.level} (${report.risk_assessment?.score}/100) | ${report.total_apis_queried} APIs | ${report.findings?.length} findings`}
                metadata={{ risk_level: report.risk_assessment?.level, sources: report.sources_used }}
              />
            </div>
          ) : (
            <Card><CardContent className="py-12 text-center text-slate-500">No investigation results yet. Run an investigation to see results here.</CardContent></Card>
          )}
        </TabsContent>

        <TabsContent value="history" className="mt-4">
          {history.length === 0 ? (
            <Card><CardContent className="py-12 text-center text-slate-500">No investigation history yet.</CardContent></Card>
          ) : (
            <div className="space-y-2">
              {history.map(inv => (
                <Card key={inv.id} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => viewReport(inv)}>
                  <CardContent className="py-3 flex items-center justify-between">
                    <div>
                      <span className="font-semibold">{inv.subject?.name}</span>
                      <span className="text-slate-500 text-sm ml-2">({inv.subject?.type})</span>
                      <span className="text-xs text-slate-400 ml-3">{new Date(inv.created_at).toLocaleDateString()}</span>
                    </div>
                    <RiskBadge level={inv.risk_assessment?.level} score={inv.risk_assessment?.score} />
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
